import { StudioNodeStyle } from '../../models/component-studio.models';

export interface StudioColorPreset {
  value: string;
  label: string;
}

export interface StudioColorPresetSection {
  id: string;
  label: string;
  presets: StudioColorPreset[];
}

export interface StudioColorFieldConfig {
  placeholder?: string;
  sections: StudioColorPresetSection[];
}

type StudioColorFieldKey = Extract<keyof StudioNodeStyle, 'color' | 'backgroundColor' | 'borderColor' | 'outlineColor'>;

const THEME_PRESETS: StudioColorPreset[] = [
  { value: 'var(--ion-text-color)', label: 'Text' },
  { value: 'var(--ion-color-primary)', label: 'Primary' },
  { value: 'var(--ion-color-secondary)', label: 'Secondary' },
  { value: 'var(--ion-color-success)', label: 'Success' },
  { value: 'var(--ion-color-warning)', label: 'Warning' },
  { value: 'var(--ion-color-danger)', label: 'Danger' }
];

const NEUTRAL_PRESETS: StudioColorPreset[] = [
  { value: '#ffffff', label: 'White' },
  { value: '#f8fafc', label: 'Slate 50' },
  { value: '#e2e8f0', label: 'Slate 200' },
  { value: '#94a3b8', label: 'Slate 400' },
  { value: '#475569', label: 'Slate 600' },
  { value: '#0f172a', label: 'Slate 900' },
  { value: '#000000', label: 'Black' }
];

const ACCENT_PRESETS: StudioColorPreset[] = [
  { value: '#2563eb', label: 'Blue 600' },
  { value: '#3b82f6', label: 'Blue 500' },
  { value: '#7c3aed', label: 'Violet 600' },
  { value: '#14b8a6', label: 'Teal 500' },
  { value: '#22c55e', label: 'Green 500' },
  { value: '#f59e0b', label: 'Amber 500' },
  { value: '#ef4444', label: 'Red 500' }
];

const SURFACE_PRESETS: StudioColorPreset[] = [
  { value: 'transparent', label: 'Transparent' },
  { value: 'rgba(15, 23, 42, 0.03)', label: 'Slate tint 3%' },
  { value: 'rgba(15, 23, 42, 0.06)', label: 'Slate tint 6%' },
  { value: 'rgba(15, 23, 42, 0.12)', label: 'Slate tint 12%' },
  { value: 'rgba(255, 255, 255, 0.08)', label: 'White tint 8%' },
  { value: 'rgba(255, 255, 255, 0.16)', label: 'White tint 16%' },
  { value: 'rgba(37, 99, 235, 0.12)', label: 'Primary tint 12%' },
  { value: 'rgba(37, 99, 235, 0.18)', label: 'Primary tint 18%' }
];

const OUTLINE_PRESETS: StudioColorPreset[] = [
  { value: 'transparent', label: 'Transparent' },
  { value: 'rgba(15, 23, 42, 0.08)', label: 'Slate line 8%' },
  { value: 'rgba(15, 23, 42, 0.16)', label: 'Slate line 16%' },
  { value: 'rgba(255, 255, 255, 0.16)', label: 'White line 16%' },
  { value: 'rgba(37, 99, 235, 0.18)', label: 'Primary line 18%' }
];

export const STUDIO_COLOR_FIELD_CONFIGS: Partial<Record<StudioColorFieldKey, StudioColorFieldConfig>> = {
  color: {
    placeholder: 'Text',
    sections: [
      { id: 'theme', label: 'Theme', presets: THEME_PRESETS },
      { id: 'neutral', label: 'Neutrals', presets: NEUTRAL_PRESETS },
      { id: 'accent', label: 'Accents', presets: ACCENT_PRESETS }
    ]
  },
  backgroundColor: {
    placeholder: 'Transparent',
    sections: [
      { id: 'surface', label: 'Surface', presets: SURFACE_PRESETS },
      { id: 'theme', label: 'Theme', presets: THEME_PRESETS },
      { id: 'neutral', label: 'Neutrals', presets: NEUTRAL_PRESETS },
      { id: 'accent', label: 'Accents', presets: ACCENT_PRESETS }
    ]
  },
  borderColor: {
    placeholder: 'Line color',
    sections: [
      { id: 'line', label: 'Lines', presets: OUTLINE_PRESETS },
      { id: 'theme', label: 'Theme', presets: THEME_PRESETS },
      { id: 'neutral', label: 'Neutrals', presets: NEUTRAL_PRESETS },
      { id: 'accent', label: 'Accents', presets: ACCENT_PRESETS }
    ]
  },
  outlineColor: {
    placeholder: 'Transparent',
    sections: [
      { id: 'line', label: 'Lines', presets: OUTLINE_PRESETS },
      { id: 'theme', label: 'Theme', presets: THEME_PRESETS },
      { id: 'neutral', label: 'Neutrals', presets: NEUTRAL_PRESETS },
      { id: 'accent', label: 'Accents', presets: ACCENT_PRESETS }
    ]
  }
};
